import React, { useContext, useMemo, useState } from 'react';
import MyProgressBar from '../basic/MyProgressBar';
import MyButton from '../basic/MyButton';
import { HelpCircle, Goal } from 'lucide-react';
import { ChildContext } from '../../context/ChildContext';
import { UserContext } from '../../context/UserContext';
import { currencies } from '../basic/currencies';
import ChildModalBalanceSheet from './ChildModalBalanceSheet';
import './ChildAccountCard.css';


const isSpecialAccount = (accountName) => {
    return accountName === "Checking" || accountName === "Savings";
};


function ChildAccountCard({ account, onViewTransactions, isCompactView }) {
    const { dashboardData } = useContext(ChildContext);
    const { user } = useContext(UserContext);
    const [isBalanceSheetModalOpen, setIsBalanceSheetModalOpen] = useState(false);

    const getCurrencySymbol = (currencyCode) => {
        const currency = currencies.find(c => c.code === currencyCode);
        return currency ? currency.symbol : '$'; // Default to '$' if not found
    };

    const currencySymbol = getCurrencySymbol(dashboardData.currencyCode);

    const calculateSplitAmount = (percentage) => {
        return ((dashboardData.weeklyAllowance * percentage) / 100).toFixed(2);
    };

    const splitAmount = useMemo(() => calculateSplitAmount(account.allowancePercentage), [dashboardData.weeklyAllowance, account.allowancePercentage]);

    const calculateWeeksToGoal = () => {
        if (account.balance >= account.goalAmount) {
            return 0; // Goal reached
        }
        if (parseFloat(splitAmount) <= 0) {
            return Infinity; // No weekly contribution
        }
        const remaining = account.goalAmount - account.balance;
        return Math.ceil(remaining / parseFloat(splitAmount));
    };

    const weeksToGoal = calculateWeeksToGoal();

    const tooltipText = useMemo(() => `
        <p><strong>Income splits settings</strong> determine how deposits such as allowances will be distributed between the accounts.</p>
        <p>For example, with the current allowance amount, this account will receive <strong>${currencySymbol}${calculateSplitAmount(account.allowancePercentage)}</strong> as it is configured for a <strong>${account.allowancePercentage}%</strong> split.</p>
    `, [currencySymbol, account.allowancePercentage, dashboardData.weeklyAllowance]);

    const goalProgress = account.goalAmount ? (account.balance / account.goalAmount) * 100 : 0;
    const isSpecial = isSpecialAccount(account.accountName);

    return (
        <div className={`child-account-card ${isSpecial ? 'special-account' : ''} ${isCompactView ? 'compact-view' : ''}`}>
            <div className="account-details">
                <div className="account-header">
                    <h3 className="account-name">
                        {account.accountName}
                        {account.goalAmount > 0 && (
                            <Goal size={20} className="goal-icon" />
                        )}
                    </h3>
                    
                    <p className="account-balance">Balance: <strong>{currencySymbol}{account.balance.toFixed(2)}</strong></p>
                </div>
            </div>
            
            {!isCompactView && (
                <>
                    <div className={`goal-details saving-goal-container ${!account.goalAmount ? 'empty' : ''}`}>
                        {account.goalAmount > 0 && (
                            <>
                            <div className="saving-goal-header">
                                <h4 className="saving-goal-label">Saving Goal</h4>
                                <p className="saving-goal-amount">{currencySymbol}{account.goalAmount.toFixed(0)}</p>
                            </div>
                            <MyProgressBar
                                className="saving-goal-progress"
                                value={account.balance}
                                max={account.goalAmount}
                                label={`${goalProgress.toFixed(0)}%`}
                                splitAmount={splitAmount}
                            />
                            <p className="weeks-to-goal">
                                {weeksToGoal === 0 ? 'Goal reached!' : 
                                weeksToGoal === Infinity ? 'N/A (no weekly contribution)' :
                                `${weeksToGoal} week${weeksToGoal !== 1 ? 's' : ''} to goal`}
                            </p>
                            </>
                        )}
                    </div>

                    <div className="split-details income-split-container">
                        <div className="income-split-header">
                            <h4 className="income-split-label">
                                Income Split
                                <span className="tooltip-container">
                                    <HelpCircle size={16} className="tooltip-icon" />
                                    <span className="tooltip-text" dangerouslySetInnerHTML={{ __html: tooltipText }}></span>
                                </span>
                            </h4>
                            <p className="mobile-only income-split-percentage">{account.allowancePercentage}%</p>
                        </div>
                        {account.allowancePercentage > 0 ? (
                            <div className="income-splits-circle desktop-only">
                                <div 
                                    className="pie-chart" 
                                    style={{ 
                                        background: `conic-gradient(#3498db 0deg, #3498db ${account.allowancePercentage * 3.6}deg, #eee ${account.allowancePercentage * 3.6}deg)` 
                                    }}
                                >
                                    <span className="pie-chart-text">{account.allowancePercentage}%</span>
                                </div>
                            </div>
                        ) : <p className="no-splits">No splits defined</p>}
                    </div>                    
                    <div className="account-actions">
                        <div className="account-buttons-stack">
                            <MyButton 
                                text="Transactions" 
                                onClick={() => onViewTransactions(account)}
                                className="mobile-transaction-button"
                                backgroundColor="#4682b4"
                                color="white"
                                hoverBackgroundColor="#36648b"
                            />
                            {account.accountName === "Checking" && (
                                <MyButton 
                                    text="Balance" 
                                    onClick={() => setIsBalanceSheetModalOpen(true)}
                                    className="mobile-transaction-button"
                                    backgroundColor="#4682b4"
                                    color="white"
                                    hoverBackgroundColor="#36648b"
                                />
                            )}
                        </div>
                    </div>
                </>
            )}

            {account.accountName === "Checking" && (
                <ChildModalBalanceSheet
                    isOpen={isBalanceSheetModalOpen}
                    onClose={() => setIsBalanceSheetModalOpen(false)}
                    childId={user.details.user_id}
                />
            )}
        </div>
    );
}

export default ChildAccountCard;